/**
 * @module getAPIBase
 */

/**
 * @typedef {object} MgnlEnvVars
 * @property {string} REACT_APP_CMS - Default CMS base URL used if Author or Public variables not defined (e.g. 'https://author-live-lc-prod.de.magnolia-cloud.com').
 * @property {string} REACT_APP_MGNL_AUTHOR - Magnolia Author base URL (e.g. 'https://public-live-lc-prod.de.magnolia-cloud.com').
 * @property {string} REACT_APP_MGNL_BASE_AUTHOR - Magnolia Author base path (e.g. '').
 * @property {string} REACT_APP_MGNL_BASE_PUBLIC - Magnolia Public base path (e.g. '').
 * @property {string} REACT_APP_MGNL_HOST - Magnolia Public base URL (e.g. 'https://author-live-lc-prod.de.magnolia-cloud.com').
 */

/**
 * Convenience function to determine whether or not in Magnolia Author environment.
 *
 * @param {MgnlEnvVars} mgnlEnvVars - Object of required Magnolia ENV variables. If not set, defaults and attempts to use `process.env`.
 *
 * @returns {boolean} Boolean flag denoting whether or not in Magnolia Author environment.
 */
function isMagnoliaAuthor(mgnlEnvVars) {
  if (
    typeof window !== 'undefined' &&
    window.location &&
    window.location.href
  ) {
    return Boolean(
      window.location.href.indexOf(mgnlEnvVars.REACT_APP_MGNL_AUTHOR) >= 0 ||
        document?.referrer.indexOf(mgnlEnvVars.REACT_APP_MGNL_AUTHOR) >= 0,
    );
  }
  return false;
}

/**
 * Convenience function to determine whether or not in Magnolia Public environment.
 *
 * @param {MgnlEnvVars} mgnlEnvVars - Object of required Magnolia ENV variables. If not set, defaults and attempts to use `process.env`.
 *
 * @returns {boolean} Boolean flag denoting whether or not in Magnolia public environment.
 */
function isMagnoliaPublic(mgnlEnvVars) {
  if (
    typeof window !== 'undefined' &&
    window.location &&
    window.location.href
  ) {
    return Boolean(
      window.location.href.indexOf(mgnlEnvVars.REACT_APP_MGNL_HOST) >= 0 ||
        document?.referrer.indexOf(mgnlEnvVars.REACT_APP_MGNL_HOST) >= 0,
    );
  }
  return false;
}

/**
 * Convenience function to return the base API URL for Magnolia.
 *
 * @param {MgnlEnvVars} mgnlEnvVars - Object of required Magnolia ENV variables. If not set, defaults and attempts to use `process.env`.
 *
 * @returns {string} The Magnolia API base URL.
 */
function getAPIBase(mgnlEnvVars) {
  const envVars = mgnlEnvVars || process.env;
  if (isMagnoliaAuthor(envVars)) {
    return (
      envVars.REACT_APP_MGNL_AUTHOR +
      (envVars.REACT_APP_MGNL_BASE_AUTHOR
        ? envVars.REACT_APP_MGNL_BASE_AUTHOR
        : '')
    );
  }
  if (isMagnoliaPublic(envVars)) {
    return (
      envVars.REACT_APP_MGNL_HOST +
      (envVars.REACT_APP_MGNL_BASE_PUBLIC
        ? envVars.REACT_APP_MGNL_BASE_PUBLIC
        : '')
    );
  }
  return envVars.REACT_APP_CMS;
}

module.exports = {
  getAPIBase,
  isMagnoliaAuthor,
  isMagnoliaPublic,
};
